import { IChargeSplitRule } from 'reducers/athena/model';

/**
 * Charge-split feature — generic, config-driven.
 *
 * A charge-split rule (from finopsconfig/chargesplitrules) can require a charge to be
 * split across a shipment "level" (SBK today, FCR/CBL in future). When a rule matches,
 * the Add Charge Item screen shows a multi-select table of that level's entities (with a
 * per-entity quantity), one adhoc-contractual request/response entry is produced per
 * selected entity, and one charge row per entity is written back into the grid.
 *
 * Adding a new level in future = add one entry to SPLIT_LEVEL_REGISTRY. No other file
 * needs to change.
 */

/** A selectable split entity (one SBK / FCR / ...). */
export interface ISplitRow {
    id: number | string;
    number: string;        // the reference value sent in the request (SBK no. / FCR no. ...)
    quantity: number;      // derived, read-only
    select?: boolean;
    // SBK x lane split — present only when the rule expands the SBK by lane. The table shows the
    // SBK once; one adhoc request entry is sent per SBK x lane (see useCreateAthenaRateRequest).
    lanes?: Array<string | null>;
    pickupLocation?: string | null;
    incoTerm?: string | null;       // SBK primary[-secondary] incoterm
}

/** Options that control how a strategy expands its entities into rows. */
export interface ISplitRowOptions {
    /** GCA shipment: lane comes from the SBK itself; otherwise from the equipments' lane ids. */
    isGca?: boolean;
    /** Distinct lane ids from etsDetails.equipments[].laneIds (non-GCA fallback when a booking has no clrLaneIds). */
    equipmentLaneIds?: string[];
}

export const TRIGGER_TYPES = { Always: 'Always', AnyBookingFlag: 'AnyBookingFlag' };
export const SPLIT_BASES = { Cost: 'Cost', Rev: 'Rev', RevCost: 'Rev_Cost' };

export interface ISplitLevelStrategy {
    /** referenceType sent on each addedCharges entry, e.g. 'SBK'. */
    referenceType: string;
    /** Table title shown above the selection table, e.g. 'Booking' for SBK, 'FCR' for FCR. */
    title: string;
    /** Column header for the reference-number column in the selection table. */
    numberLabel: string;
    /**
     * Build the selectable rows from the booking-details response (export-job/{id}/bookings),
     * i.e. the redux `exportJob.exportDetailsBooking` object.
     */
    getRows: (bookingDetails: any, options?: ISplitRowOptions) => ISplitRow[];
}

/** Incoterm parts joined by '-'; empty values and '-' (the backend's "no value" placeholder) are skipped. */
const joinIncoterm = (...parts: Array<string | null | undefined>): string | null => {
    const joined = parts
        .map((v) => (v && v.trim() !== '' && v !== '-' ? v.trim() : null))
        .filter(Boolean)
        .join('-');
    return joined || null;
};

export const SPLIT_LEVEL_REGISTRY: Record<string, ISplitLevelStrategy> = {
    SBK: {
        referenceType: 'SBK',
        title: 'Booking Data',
        numberLabel: 'Booking Number',
        // Booking number and volume come from the bookings list passed in by the caller:
        //  - Import/ETS: ets.etsDetails.shipment.bookings[] (uses `bookingId` and `number`).
        //  - Export: exportJob.exportDetailsBooking.bookings[] (uses `id` and `bookingNumber`).
        // Both shapes are handled here so a single strategy covers both flows. `volume` is the
        // per-booking volume returned by each API and is carried in `quantity` (shown in the
        // Quantity field and sent as-is).
        // Each SBK row also carries its lane ids: GCA takes the SBK's own lane, pickupLocation and
        // incoterm; non-GCA takes the lane ids of the connected equipments, with pickupLocation
        // and incoTerm null.
        getRows: (bookingDetails: any, options?: ISplitRowOptions): ISplitRow[] => {
            const bookings = bookingDetails?.bookings || [];
            const isGca = !!options?.isGca;
            const equipmentLaneIds = Array.from(new Set((options?.equipmentLaneIds || []).filter(Boolean)));
            // One row per SBK (the table shows each SBK once); the lanes ride along on the row and
            // are expanded into one request entry per SBK x lane when rates are retrieved.
            return bookings.map((b: any) => {
                // Non-GCA lane source: the booking's own connected equipments when it carries them
                // (Export), otherwise the shipment-level lane ids passed in (Import/ETS).
                const bookingLaneIds = Array.isArray(b.equipments)
                    ? Array.from(new Set<string>(
                        b.equipments.reduce((ids: string[], e: any) => ids.concat(e?.laneIds || []), []).filter(Boolean),
                    ))
                    : equipmentLaneIds;
                // Non-GCA: ETS bookings carry the lanes of their CLRs in `clrLaneIds` (array); that wins
                // over the shipment-level / equipment lanes. GCA keeps the booking's own `laneId`.
                const clrLaneIds: string[] = Array.isArray(b.clrLaneIds)
                    ? Array.from(new Set<string>(b.clrLaneIds.filter(Boolean)))
                    : [];
                let lanes: Array<string | null> = [null];
                if (isGca) {
                    lanes = [b.laneId || null];
                } else if (clrLaneIds.length) {
                    lanes = clrLaneIds;
                } else if (bookingLaneIds.length) {
                    lanes = bookingLaneIds;
                }
                return {
                    id: b.id ?? b.bookingId,
                    number: b.bookingNumber || b.number,
                    quantity: Number(b.volume) || 0,
                    lanes,
                    // pickupLocation / incoTerm are populated for GCA shipments only; non-GCA is null.
                    pickupLocation: isGca ? (b.pickupLocation || null) : null,
                    // main (booking) incoterm + primary + secondary, e.g. 'CIF-FCA-X'. Export
                    // bookings expose the main one as `incoTerms`.
                    incoTerm: isGca
                        ? joinIncoterm(b.incoTerms ?? b.incoTerm, b.primaryIncoterm, b.secondaryIncoterm)
                        : null,
                };
            });
        },
    },
    // FUTURE — no other file changes required to enable a new level, e.g.:
    // FCR: {
    //     referenceType: 'FCR',
    //     title: 'FCR Data',
    //     numberLabel: 'FCR Number',
    //     getRows: (bookingDetails: any): ISplitRow[] =>
    //         (bookingDetails?.fcrs || []).map((f: any) => ({
    //             id: f.fcrId,
    //             number: f.fcrNumber,
    //             quantity: Number(f.volume) || 0,
    //         })),
    // },
};

/**
 * Find the enabled charge-split rule that applies to a charge, if any.
 * A party can have both a specific charge-code rule and a '*' (all charges) rule, so the
 * specific charge code takes precedence: match the exact charge code first, and only fall
 * back to the '*' rule when no specific rule exists.
 */
export const resolveChargeSplitRule = (
    rules: IChargeSplitRule[] | undefined | null,
    params: { contractualParty?: string; materialCode?: string | number; shipmentType?: string },
): IChargeSplitRule | undefined => {
    if (!rules?.length) {
        return undefined;
    }
    const cp = params.contractualParty?.toLowerCase();
    const shipmentType = params.shipmentType?.toLowerCase();
    const materialCode = params.materialCode != null ? String(params.materialCode) : '';
    const candidates = rules.filter(
        (r) =>
            r.isEnabled &&
            r.contractualParty?.toLowerCase() === cp &&
            r.shipmentType?.toLowerCase() === shipmentType,
    );
    // Specific charge code wins; otherwise use the '*' (all charges) rule.
    return (
        candidates.find((r) => String(r.chargeCode) === materialCode) ||
        candidates.find((r) => r.chargeCode === '*')
    );
};

/**
 * Whether the rule's TriggerType asks for the split on this shipment.
 *  - Always: yes.
 *  - AnyBookingFlag: only if the ETS header flag isInvoiceSplitRequired is true.
 *  - missing (legacy rules): treated as Always.
 */
export const shouldTriggerSplit = (rule: IChargeSplitRule | undefined, isInvoiceSplitRequired: boolean | undefined): boolean => {
    if (!rule) {
        return false;
    }
    if (!rule.triggerType || rule.triggerType.toLowerCase() === TRIGGER_TYPES.Always.toLowerCase()) {
        return true;
    }
    if (rule.triggerType.toLowerCase() === TRIGGER_TYPES.AnyBookingFlag.toLowerCase()) {
        return isInvoiceSplitRequired === true;
    }
    return false;
};

/**
 * Whether the rule's SplitBasis covers a charge of the given kind:
 *  - Rev:      revenue charges only.
 *  - Cost:     cost charges only.
 *  - Rev_Cost: both.
 *  - missing (legacy rules): both.
 */
export const isChargeInSplitBasis = (rule: IChargeSplitRule | undefined, isRevenue: boolean): boolean => {
    if (!rule) {
        return false;
    }
    const basis = rule.splitBasis?.toLowerCase();
    if (!basis || basis === SPLIT_BASES.RevCost.toLowerCase()) {
        return true;
    }
    if (basis === SPLIT_BASES.Rev.toLowerCase()) {
        return isRevenue;
    }
    if (basis === SPLIT_BASES.Cost.toLowerCase()) {
        return !isRevenue;
    }
    return false;
};

/**
 * Resolve the active strategy for a charge, or null if the charge is not split
 * (rule missing/disabled, or split level not yet supported by the registry).
 */
export const resolveSplitStrategy = (
    rules: IChargeSplitRule[] | undefined | null,
    params: { contractualParty?: string; materialCode?: string | number; shipmentType?: string },
): ISplitLevelStrategy | null => {
    const rule = resolveChargeSplitRule(rules, params);
    if (!rule) {
        return null;
    }
    return SPLIT_LEVEL_REGISTRY[rule.splitLevel] ?? null;
};
